import React, { useCallback, useState } from 'react';
import LoadingSpinner from './LoadingSpinner';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import { Field, Input } from '../ui/Field/Field';
import { toast } from '../ui/feedback';
import { finesService, errorMessage } from '../services/finesService';

// "Ejar account" card: each user saves his own Ejar login once, so what he registers or saves
// on Ejar (The Engine, Customer automation, Vehicles) appears under HIS name, not the server account.
// The password goes to the engine over HTTPS, is verified with a real Ejar login,
// encrypted at rest on the server, and never stored in the browser or echoed back.
// ``onlyWhenMissing``: shown only to a user who has not saved his own Ejar login yet (the Vehicles page - moving a car
// is a write on Ejar, made under the user's own login; owner, 5 Oct 2026)
export default function EjarAccountCard({ onlyWhenMissing = false } = {}) {
    const [me, setMe] = useState(null);
    const [user, setUser] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    const load = useCallback(async () => {
        try {
            const m = await finesService.ejarMe();
            setMe(m);
            if (m.mustConfigure) setOpen(true);   // non-admin without a login: the form is the first thing to do
        } catch (e) { setMe({ error: errorMessage(e) }); }
    }, []);
    React.useEffect(() => { load(); }, [load]);
    const save = async () => {
        if (!user.trim() || !password) return;
        setBusy(true);
        try {
            await finesService.ejarMeSave(user.trim(), password);
            setPassword(''); setOpen(false);
            await load();
            toast.success(`Ejar login verified — your registrations will be under "${user.trim()}"`);
        } catch (e) { toast.error(errorMessage(e)); }
        setBusy(false);
    };
    // inline two-step confirmation: browsers suppress window.confirm after a few
    // dialogs in one session, which made "remove" look dead
    const [confirmRemove, setConfirmRemove] = useState(false);
    const remove = async () => {
        setBusy(true);
        try { await finesService.ejarMeRemove(); setConfirmRemove(false); setOpen(true); await load(); toast.success('Your Ejar login was removed — enter your own login to register again.'); }
        catch (e) { toast.error(errorMessage(e)); }
        setBusy(false);
    };
    if (onlyWhenMissing && !(me && me.mustConfigure)) return null;
    return (
        <Card>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-900">
            <h2 className="text-sm font-semibold text-gray-900">Ejar account</h2>
            {me?.error ? <span className="text-red-700 text-sm">{me.error}</span> : me ? (
                me.mustConfigure ? (
                    <span className="text-sm font-semibold text-red-700">Enter your own Ejar username and password before registering anything — every entry on Ejar is made under the user who enters it (the server account only reads).</span>
                ) : (
                    <span className="inline-flex flex-wrap items-center gap-2 text-sm">
                        <span>registering on Ejar as <b className="font-mono">{me.effectiveUser || '—'}</b></span>
                        <Badge variant="success">your account</Badge>
                    </span>
                )
            ) : <span className="inline-flex items-center gap-2 text-sm text-gray-600"><LoadingSpinner size="sm" />loading…</span>}
            <span className="ml-auto flex flex-wrap items-center gap-2">
                {me?.own && !confirmRemove && <Button size="sm" variant="ghost" className="text-red-700" onClick={() => setConfirmRemove(true)} disabled={busy}>Remove</Button>}
                {me?.own && confirmRemove && (
                    <span role="group" aria-label="Confirm removal" className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-semibold text-red-700">Remove your Ejar login?</span>
                        <Button size="sm" onClick={() => setConfirmRemove(false)} disabled={busy}>No</Button>
                        <Button size="sm" variant="danger" onClick={remove} disabled={busy} isLoading={busy}>Yes, remove</Button>
                    </span>
                )}
                <Button size="sm" leadingIcon={<Icon glyph={ICONS.key} size="sm" />} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
                    {me?.own ? 'Change my Ejar login' : 'Use my own Ejar login'}
                </Button>
            </span>
            {/* autocomplete "username"/"current-password": the browser's password manager may
                offer to remember the Ejar login (like on the Ejar site); the engine itself keeps
                it encrypted on the server, so it is entered ONCE - the form is only for changes. */}
            {open && (
                <form name="ejar-login" className="w-full flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3" onSubmit={(e) => { e.preventDefault(); save(); }}>
                    <Field label="Ejar username">
                        <Input name="username" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="username" className="w-full sm:w-44" />
                    </Field>
                    <Field label="Ejar password">
                        <Input name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" className="w-full sm:w-44" />
                    </Field>
                    <Button type="submit" variant="primary" disabled={busy || !user.trim() || !password} isLoading={busy}>{busy ? 'Verifying…' : 'Verify & save'}</Button>
                    <span className="text-sm text-gray-600 basis-full">Entered once: verified with a real Ejar login and kept encrypted on the engine server for all your future registrations (from any device). Your browser may also offer to remember it.</span>
                </form>
            )}
          </div>
        </Card>
    );
}
